// ============================================================
// MOONSHINE MASTERS — still.js        THE STILL (pure physics)
//
// 2026-10-23, the still update (from the distilling research caP'n brought in).
// No React, no three, no Date.now(): a run is advanced to a time you pass in, in
// fixed STEP_MS steps, so it is deterministic, testable in Node, and the same on
// every client (co-op later). Every number lives in moonshineData.js (STILL, PARTS).
//
// A RUN is a stream from prog 0 (first drip) to 1 (dry). What drips depends on where
// the stream is (the head part's cuts):  foreshots f -> heads h -> HEARTS H -> tails t.
// It drips into the jar that's under the spout (row[cur]); SWAP puts a fresh one there.
// THE FIRE: speed + foam. Foam over CLOUDY_AT = cloudy drips; foam at 1 = a CLOG.
// A clog builds pressure: flour paste POPS (that jar is lost, the run goes on),
// clamps hold... until BOOM (the run is gone and the still is busted).
// When it runs dry (or you STOP it) you BLEND: pick jars, get jars of shine.
//
//   advance(run, now) -> { run, events }     events: "clog" | "pop" | "boom" | "dry"
// ============================================================
import { STILL, PARTS, RECIPES, FRACTIONS, TUNING, RATING, YEASTS, MASH, PROOF, masterMax, proofOf } from "./moonshineData";

const cutsOf = (rig) => (PARTS.head[rig && rig.head] || PARTS.head.pot).cuts;
export const fracAt = (p, c) => (p < c.f ? "f" : p < c.h ? "h" : p < c.H ? "H" : "t");
export const emptyJar = () => ({ f: 0, h: 0, H: 0, t: 0, c: 0, cH: 0, popped: false });
export const jarVol = (j) => j.f + j.h + j.H + j.t;
const streamS = (run) => RECIPES[run.recipe].streamS || STILL.STREAM_S;
const clamp01 = (x) => Math.max(0, Math.min(1, x));

// How many jars of HEARTS this run holds, before it starts (the extras are the bonuses)
export function runJars(barrel, extra = {}) {
  return barrel.size + (barrel.malt === "good" ? TUNING.MALT_BONUS : 0)
    + (extra.thump ? STILL.THUMP_JARS : 0) + (extra.feints || 0) * STILL.FEINT_JARS;
}

// A new run on the still. `barrel` = the fermented barrel; rig = the still's parts right now (snapshotted -
// you can't swap a part mid-run); extra = { thump: bool, flavor, feints: n }.
export function newRun(barrel, rig, now, extra = {}) {
  const c = cutsOf(rig), jars = runJars(barrel, extra);
  // EVERY STEP COUNTS (2026-10-25): the mash's marks ride along to the blend. eff = how much of the wash really converted
  // (the water line, the pitch) - a thin wash or a cooked yeast simply makes less (missing = 1, an old barrel)
  const eff = Number.isFinite(barrel.eff) ? Math.max(MASH.EFF_MIN, Math.min(1, barrel.eff)) : 1;
  return {
    recipe: barrel.recipe, rushed: !!barrel.rushed, catalyst: barrel.catalyst || null, size: barrel.size, malt: barrel.malt || null,
    jars, total: (jars / (c.H - c.h)) * eff,         // the whole stream's volume (in jars): the hearts alone are exactly `jars` x eff
    eff, card: cardOf(barrel), yeast: YEASTS[barrel.yeast] ? barrel.yeast : null, thump: !!extra.thump, wept: 0, feints: extra.feints || 0, clogs: 0, pops: 0,
    rig: { ...rig }, flavor: extra.flavor || null, cursed: !!(PARTS.cond[rig.cond] || {}).cursed,
    prog: 0, foam: 0, press: 0, clog: false, fire: "med", at: now,
    row: [emptyJar()], cur: 0, done: false,
  };
}

// Advance a run to `now`. Pure: returns a NEW run (the input is never touched) and what happened on the way.
export function advance(run, now) {
  const events = [];
  if (!run || run.done || !(now > run.at)) return { run, events };
  const r = { ...run, row: run.row.map((j) => ({ ...j })) };
  const c = cutsOf(r.rig), seal = PARTS.seal[r.rig.seal] || PARTS.seal.paste, cond = PARTS.cond[r.rig.cond] || PARTS.cond.worm;
  const S = streamS(r);
  let t = r.at, guard = 0;
  while (t < now && guard++ < 40000) {
    const dtMs = Math.min(STILL.STEP_MS, now - t); t += dtMs;
    const dt = dtMs / 1000, F = STILL.FIRE[r.fire] || STILL.FIRE.med;
    if (r.clog) {                                           // CLOGGED: nothing drips, pressure builds
      r.press += F.press * dt;
      if (seal.fail === "boom" ? r.press >= STILL.BOOM_AT : r.press >= STILL.POP_AT) {
        if (seal.fail === "boom") { r.at = t; r.boom = true; events.push("boom"); return { run: r, events }; }
        r.row[r.cur] = { ...emptyJar(), popped: true };     // POP: the jar under the spout got knocked over
        r.clog = false; r.press = 0; r.foam = STILL.CLEAR_FOAM; r.pops = (r.pops || 0) + 1; events.push("pop");
      }
      continue;
    }
    r.foam = clamp01(r.foam + F.foam * dt);
    if (r.foam >= 1) { r.clog = true; r.press = 0; r.clogs = (r.clogs || 0) + 1; events.push("clog"); continue; }
    const dp = Math.min(1 - r.prog, (dt * F.speed * cond.speed) / S);
    const k = fracAt(r.prog + dp / 2, c);
    let dv = dp * r.total;
    if (seal.leaks) { const w = dv * (STILL.LEAK[r.fire] || 0); dv -= w; r.wept = (r.wept || 0) + w; }   // flour paste weeps (more on a hot fire)
    const j = r.row[r.cur];
    j[k] += dv;
    if (r.foam > STILL.CLOUDY_AT) { j.c += dv; if (k === "H") j.cH += dv; }
    r.prog += dp;
    if (r.prog >= 1 - 1e-9) { r.prog = 1; r.done = true; events.push("dry"); break; }
  }
  r.at = now;
  return { run: r, events };
}
export const peek = (run, now) => advance(run, now).run;     // look without keeping (the HUD, the world's FX)

// ── the player's hands ───────────────────────────────────────
export function swapJar(run) {          // a fresh jar under the spout (no-op on the last jar of the row)
  if (run.done || run.cur >= STILL.ROW - 1) return run;
  return { ...run, row: [...run.row, emptyJar()], cur: run.cur + 1 };
}
export const setFire = (run, fire) => (STILL.FIRE[fire] ? { ...run, fire } : run);
export const clearLine = (run) => (run.clog ? { ...run, clog: false, press: 0, foam: STILL.CLEAR_FOAM } : run);
export const stopRun = (run) => ({ ...run, done: true, clog: false, press: 0 });    // what's left in the pot is lost

// ── reading a run ────────────────────────────────────────────
// where the stream is: fore | heads | hearts | turning | tails | dry | clog
export function phaseOf(run) {
  if (!run) return "idle";
  if (run.done) return "dry";
  if (run.clog) return "clog";
  const c = cutsOf(run.rig), k = fracAt(run.prog, c);
  if (k === "H" && run.prog >= c.H - STILL.TURN) return "turning";
  return { f: "fore", h: "heads", H: "hearts", t: "tails" }[k];
}
export const isCloudy = (run) => !!run && run.foam > STILL.CLOUDY_AT;
// does the jar under the spout NEED swapping? = it already holds a different cut than what's dripping now
// (2026-10-24 tutorial review: the hearts arrived and nothing said "swap") - so "swap!" only shows when it matters
export function needsSwap(run) {
  if (!run || run.done || run.clog || run.cur >= STILL.ROW - 1) return false;
  const j = run.row[run.cur], k = fracAt(run.prog, cutsOf(run.rig));
  return jarVol(j) - j[k] > 0.02;
}
export const smellOf = (k, recipe) => (k === "H" ? RECIPES[recipe].smell || "sweet corn" : FRACTIONS[k].smell);

// what a jar is, in a friend's words: its main smell, a hint of the next, and how it looks
export function describeJar(j, recipe) {
  const v = jarVol(j);
  if (v < 0.02) return { empty: true, main: null, line: j.popped ? "Knocked over in the pop." : "Empty.", look: "clear" };
  const parts = ["f", "h", "H", "t"].map((k) => [k, j[k] / v]).sort((a, b) => b[1] - a[1]);
  const [m, s] = parts;
  const look = j.c / v > 0.2 ? "cloudy" : j.t / v > 0.35 ? "oily" : "clear";
  const hint = s[1] > 0.15 ? `, a hint of ${smellOf(s[0], recipe)}` : "";
  return { empty: false, main: m[0], share: m[1], look, poison: j.f / v >= STILL.CURSE_AT,
    line: `${smellOf(m[0], recipe)}${hint}`, say: FRACTIONS[m[0]].say };
}

// the jars a helpful hand would pick: mostly hearts, no poison in them
export function defaultPick(run) {
  return run.row.map((j, i) => [j, i]).filter(([j]) => {
    const v = jarVol(j); return v > 0.02 && j.H / v >= 0.75 && j.f / v < 0.005;
  }).map(([, i]) => i);
}

// THE BLEND: what pouring these jars together makes. Pure - the reducer and the HUD's preview both call it.
// the marks a barrel carries into the still (only real values; anything else = not measured = perfect)
export function cardOf(b) {
  const n = (x) => (Number.isFinite(x) ? x : null);
  return { water: n(b && b.water), burn: n(b && b.burn), pitch: n(b && b.pitch), yeast: b && YEASTS[b.yeast] ? b.yeast : null };
}
// the proof the blend comes off at, before any water: the head part sets it, the thumper doubles it up, heads raise it, tails drop it
export function rawProof(run, sum) {
  const V = jarVol(sum); if (!(V > 0)) return 0;
  const head = (run.rig && run.rig.head) || "pot";
  const p = (PROOF.BASE[head] || PROOF.BASE.pot) + (run.thump ? PROOF.THUMP : 0) + PROOF.HEADS * ((sum.h + sum.f) / V) + PROOF.TAILS * (sum.t / V);
  return Math.max(PROOF.MIN, Math.min(PROOF.MAX, p));
}

// ── WILLIE'S RATING (2026-10-25): four parts, 0-100 each, weighted (RATING.W). Pure: the blend preview and the reducer both call it.
// notes = what Willie would say, worst first (the card shows a few).
export function rateRun(run, sum, cursed) {
  const rec = RECIPES[run.recipe], card = run.card || {};
  // a run from before THE FILTER existed has no filter field: neutral (missing = perfect), not "no filter"
  const filt = run.rig && run.rig.filter == null ? { cloudy: 1, mellow: 1, edge: true } : PARTS.filter[run.rig.filter] || PARTS.filter.none;
  const legacy = run.thump == null;      // an older run: its thumper / feints weren't recorded - its yardstick is its own jars
  const notes = [];
  const V = jarVol(sum);
  if (cursed || !(V > 0)) return { score: 0, parts: { purity: 0, flavor: 0, yield: 0, safety: 0 }, notes: cursed ? ["Poison. Nobody's drinking that."] : [] };
  const clamp = (x) => Math.max(0, Math.min(100, x));
  // PURITY: the hearts share, the filter softening the cloudy bits (cloth) and the harsh ends (charcoal)
  const good = Math.max(0, sum.H - sum.cH * filt.cloudy), bad = (sum.f + sum.h + sum.t) * filt.mellow + sum.cH * filt.cloudy;
  const pm = good / Math.max(1e-9, good + bad);
  const purity = clamp(((pm - RATING.PURITY_LO) / (RATING.PURITY_HI - RATING.PURITY_LO)) * 100);
  if (purity < 99.5) {                   // (notes only where the part fell short - "PERFECT" never sits next to a complaint)
    if (sum.h + sum.f > 0.02 * V) notes.push(["Heads in it - that bite's nail polish.", 30]);
    if (sum.t > 0.02 * V) notes.push(["Tails in it. Oily.", 30]);
    if (sum.cH > 0.02 * V) notes.push([filt.cloudy < 1 ? "Foamed over - the cloth caught most of it." : "Foamed over. Cloudy.", 20]);
  }
  // FLAVOR: 100, minus every mistake upstream
  let flavor = 100;
  const burnF = card.burn != null ? Math.min(MASH.BURN_MAX, card.burn * MASH.BURN_FLAVOR) : 0;
  if (burnF > 0.5) { flavor -= burnF; notes.push(["Caught on the bottom. Burnt popcorn.", burnF]); }
  const y = YEASTS[card.yeast];
  if (y && y.flavor) { flavor -= y.flavor; notes.push([card.yeast === "turbo" ? "Turbo yeast. Tastes like a chemistry set." : "Distiller's yeast - clean, but a little plain.", y.flavor]); }
  if (card.pitch != null && card.pitch > MASH.PITCH[1]) {
    const f = card.pitch > MASH.PITCH_HOT ? MASH.PITCH_COOKED_F : MASH.PITCH_HOT_F;
    flavor -= f; notes.push([card.pitch > MASH.PITCH_HOT ? "Poured it hot - cooked the yeast." : "Poured it a touch warm.", f]);
  }
  if (run.rushed) { flavor -= RATING.RUSHED_F; notes.push(["Rushed. You can taste it.", RATING.RUSHED_F]); }
  if (rec.deviance) { flavor -= RATING.DEVIANCE_F; notes.push(["Sugar and regret. It's the shortcut.", RATING.DEVIANCE_F]); }
  if (!filt.edge) { flavor -= RATING.RAW_EDGE; notes.push([filt.cloudy < 1 ? "Still a raw edge. Charcoal'd smooth it." : "A raw edge. Charcoal'd smooth it.", RATING.RAW_EDGE * 0.8]); }
  flavor = clamp(flavor);
  // YIELD: the clean hearts you kept, against what a MASTER'S full rig gets out of this batch
  const max = legacy ? run.jars || masterMax(run.size || 0, !!rec.deviance) : masterMax(run.size || 0, !!rec.deviance), kept = Math.max(0, sum.H - sum.cH);
  const yieldS = max > 0 ? clamp((kept / (max * RATING.YIELD_SLACK)) * 100 + 1e-6) : 0;
  if (!rec.deviance && run.malt !== "good") notes.push([run.malt === "miss" ? "Missed the malt." : "No malt in it.", 100 * TUNING.MALT_BONUS / max * 0.2]);
  if (!legacy && !run.thump) notes.push([run.rig && run.rig.thumper === "thumper" ? "Thumper sat empty." : "No thumper. A master's would've doubled it up.", 100 * STILL.THUMP_JARS / max * 0.2]);
  if (!legacy && (run.feints || 0) < STILL.FEINT_MAX) notes.push(["Feints left on the shelf - pour 'em back in.", 100 * (STILL.FEINT_MAX - (run.feints || 0)) * STILL.FEINT_JARS / max * 0.2]);
  if ((run.wept || 0) > 0.02 && yieldS < 99.5) notes.push(["The paste wept a little. Low fire, or clamps.", 100 * run.wept / max * 0.2]);
  if (card.water != null && Math.abs(card.water - 1) > MASH.WATER_OK) notes.push([card.water > 1 ? "Too much water. Thin wash." : "Too little water. Thick and stingy.", 15]);
  // SAFETY: every clog and pop
  const safety = clamp(100 - RATING.CLOG * (run.clogs || 0) - RATING.POP * (run.pops || 0));
  if (run.pops) notes.push(["Blew the paste. Lost a jar to it.", RATING.POP * 0.2 * run.pops]);
  else if (run.clogs) notes.push(["Clogged the line.", RATING.CLOG * 0.2 * run.clogs]);
  const parts = { purity: Math.round(purity), flavor: Math.round(flavor), yield: Math.round(yieldS), safety: Math.round(safety) };
  let score = Math.round(Object.entries(RATING.W).reduce((a, [k, w]) => a + w * (k === "yield" ? yieldS : k === "purity" ? purity : k === "flavor" ? flavor : safety), 0));
  if (score >= 100 && Object.values(parts).some((v) => v < 100)) score = 99;   // 100 means every part is 100 - rounding never gives it away
  return { score, parts, notes: notes.sort((a, b) => b[1] - a[1]).map(([t]) => t) };
}
// the rank a rating allows (RATING.Q) - a jar's quality never beats it
export function ratingCap(score) {
  const Q = RATING.Q;
  return score >= Q.legendary ? "legendary" : score >= Q.prime ? "prime" : score >= Q.fine ? "fine" : "rough";
}

const QUALITY_ORDER = ["rough", "fine", "prime", "legendary"];
export function blend(run, picks, water = 0) {
  const pick = new Set((picks || []).filter((i) => Number.isInteger(i) && i >= 0 && i < run.row.length));
  const sum = emptyJar();
  let left = 0, tossed = 0;
  run.row.forEach((j, i) => {
    const v = jarVol(j);
    if (pick.has(i)) { for (const k of ["f", "h", "H", "t", "c", "cH"]) sum[k] += j[k]; return; }
    if (v > 0.02 && j.f / v < 0.5) left += v - j.f; else if (v > 0.02) tossed++;    // poison jars are tossed; the rest is feints by volume
  });
  const feints = Math.floor(left / STILL.FEINT_UNIT + 1e-9);
  const V = jarVol(sum), rec = RECIPES[run.recipe];
  const n = V > 0 ? Math.floor(V + STILL.JAR_ROUND) : 0;
  const purity = V > 0 ? Math.max(0, sum.H - sum.cH) / V : 0;
  const cursed = n > 0 && (run.cursed || sum.f / V >= STILL.CURSE_AT);
  let q = purity >= STILL.PRIME_AT ? (run.catalyst && !rec.deviance ? "legendary" : "prime") : purity >= STILL.FINE_AT ? "fine" : "rough";
  if (run.rushed) q = "rough";                                  // a rushed ferment shows
  if (rec.deviance && (q === "prime" || q === "legendary")) q = "fine";   // the shortcut: never above fine
  const yq = YEASTS[(run.card && run.card.yeast) || run.yeast];
  if (yq && yq.capQ && (q === "prime" || q === "legendary")) q = yq.capQ;
  // (the line above is turbo's: quick, never better than fine)
  if (rec.capQ && QUALITY_ORDER.indexOf(q) > QUALITY_ORDER.indexOf(rec.capQ)) q = rec.capQ;   // 2026-10-31: the House Blend is never better than fine  // turbo: quick, never better than fine (review 2026-10-25: it farmed prime)
  // EVERY STEP COUNTS: the rating caps the quality (a sloppy run can't come out prime however clean the cut)
  const rating = rateRun(run, sum, cursed);
  const QS = ["rough", "fine", "prime", "legendary"], cap = ratingCap(rating.score);
  if (n > 0 && !cursed && QS.indexOf(q) > QS.indexOf(cap)) q = cap;
  const w = Math.max(0, Math.min(PROOF.WATER_MAX, Number(water) || 0)), raw = rawProof(run, sum);
  return { jars: n, q, cursed, purity, feints, tossed, flavor: run.flavor, rating, proof: n > 0 ? proofOf(raw, w) : 0, rawProof: Math.round(raw), water: w,
    max: Math.floor(masterMax(run.size || 0, !!rec.deviance) + STILL.JAR_ROUND) };
}
